import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ColorPicker,
  ColorArea,
  ColorSlider,
  ColorSwatch,
  ColorField,
  Slider,
  parseColor,
  type Color,
} from '@heroui/react';
import {
  contrastRatio,
  defaultBrightness,
  deriveTheme,
  DEFAULT_CARDS,
  type DerivedPalette,
} from '@bunker/shared/theme';
import {
  DEFAULT_SEED,
  cispracsPalette,
  cispracsLightPalette,
  getActiveSeed,
  hasThemeChoice,
  previewCustomPalette,
  previewDefaultTheme,
  resetTheme,
  setCustomPalette,
  type CustomPalette,
  type ThemeMode,
  type ThemeSelection,
} from '@/lib/theme';
import { Segmented } from '@/components/Segmented';
import { CheckIcon } from '@/components/icons';

/**
 * Настройки темы — one coherent control instead of three systems in a pile.
 *
 * The two base modes and preview stay in view. Palette choices remain available
 * behind one disclosure, so the everyday setting is quick to scan:
 *
 *   1. Режим — dark or light.
 *   2. Optional background and accent colours, shown on demand.
 *
 * Brightness, card contrast and a custom background colour are real but rare
 * wishes, so they live behind «Тонкая настройка». One quiet «Сбросить» returns
 * the studio default (graphite + copper).
 *
 * Mechanics kept from the fix before: the picker keeps its own Color so the hue
 * survives black/white/grey; the app repaints on pauses (the in-dialog preview
 * follows every move); saving waits until the controls settle and our own save
 * echoing back never snaps the controls.
 */

interface Tone {
  id: string;
  /** Only hue and saturation are used — lightness comes from mode/brightness. */
  hex: string;
  labelKey: string;
}

const TONES: Tone[] = [
  { id: 'graphite', hex: DEFAULT_SEED.base.toLowerCase(), labelKey: 'settings.theme.tone.graphite' },
  { id: 'neutral', hex: '#26272a', labelKey: 'settings.theme.tone.neutral' },
  { id: 'slate', hex: '#23344a', labelKey: 'settings.theme.tone.slate' },
  { id: 'ocean', hex: '#1d4556', labelKey: 'settings.theme.tone.ocean' },
  { id: 'forest', hex: '#27463a', labelKey: 'settings.theme.tone.forest' },
  { id: 'sand', hex: '#54432f', labelKey: 'settings.theme.tone.sand' },
  { id: 'wine', hex: '#4c2733', labelKey: 'settings.theme.tone.wine' },
  { id: 'plum', hex: '#3b2c52', labelKey: 'settings.theme.tone.plum' },
];

/** Calm accents with similar weight, so none of them shouts next to the rest. */
const ACCENTS: { hex: string; labelKey: string }[] = [
  { hex: DEFAULT_SEED.accent.toLowerCase(), labelKey: 'settings.theme.accentNames.copper' },
  { hex: '#e5896a', labelKey: 'settings.theme.accentNames.coral' },
  { hex: '#dcb05a', labelKey: 'settings.theme.accentNames.amber' },
  { hex: '#6cbf92', labelKey: 'settings.theme.accentNames.mint' },
  { hex: '#4fb3c8', labelKey: 'settings.theme.accentNames.teal' },
  { hex: '#5b8def', labelKey: 'settings.theme.accentNames.blue' },
  { hex: '#9483ee', labelKey: 'settings.theme.accentNames.lavender' },
  { hex: '#df7aa3', labelKey: 'settings.theme.accentNames.rose' },
];

const SAVE_DELAY_MS = 350;
/** Repainting the whole app restyles every element (≈12 ms on the mock, many
 *  times that on a real server). While dragging, the preview here follows every
 *  move; the app repaints when the pointer rests, at least every max-wait. */
const PAINT_IDLE_MS = 90;
const PAINT_MAX_WAIT_MS = 300;

type Draft = Required<CustomPalette>;

function fullSeed(): Draft {
  const seed = getActiveSeed();
  return {
    accent: seed.accent.toLowerCase(),
    base: seed.base.toLowerCase(),
    mode: seed.mode,
    brightness: seed.brightness ?? defaultBrightness(seed.mode),
    cards: seed.cards ?? DEFAULT_CARDS,
  };
}

const near = (a: number, b: number) => Math.abs(a - b) < 0.005;

/** The studio default: dark graphite with copper and untouched knobs. It is
 *  applied as the hand-tuned palette rather than a derived approximation. */
function isDefaultLook(d: Draft): boolean {
  return (
    d.mode === 'dark' &&
    d.base === DEFAULT_SEED.base.toLowerCase() &&
    d.accent === DEFAULT_SEED.accent.toLowerCase() &&
    near(d.brightness, defaultBrightness('dark')) &&
    near(d.cards, DEFAULT_CARDS)
  );
}

export function ThemeStudio(props: { selection: ThemeSelection }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(fullSeed);
  const [customOpen, setCustomOpen] = useState(false);
  const [fineOpen, setFineOpen] = useState(false);
  const draftRef = useRef(draft);
  const saveTimer = useRef<number | null>(null);
  const paintTimer = useRef<number | null>(null);
  const lastPaint = useRef(0);
  const lastSaved = useRef<string | null>(null);

  // A change from elsewhere (the top-bar day/night toggle) re-seeds the
  // controls; our own save echoing back does not.
  useEffect(() => {
    const seed = fullSeed();
    if (JSON.stringify(seed) === lastSaved.current) return;
    draftRef.current = seed;
    setDraft(seed);
  }, [props.selection]);

  useEffect(
    () => () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      if (paintTimer.current !== null) window.clearTimeout(paintTimer.current);
    },
    [],
  );

  function paint(d: Draft) {
    if (isDefaultLook(d)) previewDefaultTheme();
    else previewCustomPalette(d);
  }

  function change(patch: Partial<Draft>) {
    const next = { ...draftRef.current, ...patch };
    draftRef.current = next;
    setDraft(next);
    if (paintTimer.current !== null) window.clearTimeout(paintTimer.current);
    const overdue = performance.now() - lastPaint.current >= PAINT_MAX_WAIT_MS;
    paintTimer.current = window.setTimeout(
      () => {
        paintTimer.current = null;
        lastPaint.current = performance.now();
        paint(draftRef.current);
      },
      overdue ? 0 : PAINT_IDLE_MS,
    );
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      const d = draftRef.current;
      lastSaved.current = JSON.stringify(d);
      if (isDefaultLook(d)) resetTheme();
      else setCustomPalette(d);
    }, SAVE_DELAY_MS);
  }

  function reset() {
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    if (paintTimer.current !== null) window.clearTimeout(paintTimer.current);
    saveTimer.current = null;
    paintTimer.current = null;
    lastSaved.current = null;
    resetTheme();
  }

  const derive = (d: Draft) => deriveTheme({ ...d, keepAccent: true });
  const palette = useMemo(() => {
    if (isDefaultLook(draft)) return cispracsPalette;
    if (props.selection.kind === 'preset' && props.selection.id === 'parchment' &&
        draft.mode === 'light' && draft.base === DEFAULT_SEED.base.toLowerCase() &&
        draft.accent === cispracsLightPalette.accent.toLowerCase() &&
        near(draft.brightness, defaultBrightness('light')) && near(draft.cards, DEFAULT_CARDS)) {
      return cispracsLightPalette;
    }
    return derive(draft);
  }, [draft, props.selection]); // eslint-disable-line react-hooks/exhaustive-deps
  const customTone = !TONES.some((tone) => tone.hex === draft.base);
  const customAccent = !ACCENTS.some((a) => a.hex === draft.accent);
  const tuned = !near(draft.brightness, defaultBrightness(draft.mode)) || !near(draft.cards, DEFAULT_CARDS) || customTone;
  const canReset = hasThemeChoice() || !isDefaultLook(draft);

  return (
    <div className="app-theme-studio mt-4 flex flex-col gap-6">
      <figure className="m-0 flex flex-col gap-2">
        <figcaption className="text-xs text-ink-muted">{t('settings.theme.preview')}</figcaption>
        <AppPreview palette={palette} />
      </figure>

      <Row
        label={t('settings.theme.mode')}
        aside={
          canReset ? (
            <button
              type="button"
              onClick={reset}
              className="min-h-11 rounded-lg px-2.5 py-2 text-xs font-medium text-ink-muted outline-none transition-colors hover:bg-white/[0.06] hover:text-ink focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t('settings.theme.reset')}
            </button>
          ) : null
        }
      >
        <Segmented<ThemeMode>
          label={t('settings.theme.mode')}
          value={draft.mode}
          onChange={(mode) => change({ mode, brightness: defaultBrightness(mode) })}
          options={[
            { value: 'dark', label: t('settings.theme.dark') },
            { value: 'light', label: t('settings.theme.light') },
          ]}
        />
      </Row>

      <button
        type="button"
        aria-expanded={customOpen}
        onClick={() => setCustomOpen((open) => !open)}
        className="flex min-h-11 items-center justify-between rounded-lg px-1 text-left text-sm font-medium text-ink outline-none transition-colors hover:text-accent focus-visible:ring-2 focus-visible:ring-accent"
      >
        <span>{t('settings.theme.customize')}</span>
        <span aria-hidden className={'text-lg text-ink-muted transition-transform ' + (customOpen ? 'rotate-90' : '')}>›</span>
      </button>

      {customOpen && <div className="flex flex-col gap-6">
      <Row label={t('settings.theme.background')}>
        <div
          role="radiogroup"
          aria-label={t('settings.theme.background')}
          className="grid grid-cols-2 gap-2.5 min-[360px]:grid-cols-3 min-[540px]:grid-cols-4"
        >
          {TONES.map((tone) => {
            const name = t(tone.labelKey);
            return (
              <ToneCard
                key={tone.id}
                name={name}
                palette={derive({ ...draft, base: tone.hex })}
                selected={draft.base === tone.hex}
                onSelect={() => change({ base: tone.hex })}
              />
            );
          })}
        </div>
      </Row>

      <Row label={t('settings.theme.accent')}>
        <div role="radiogroup" aria-label={t('settings.theme.accent')} className="flex flex-wrap items-center gap-2.5">
          {ACCENTS.map((a) => {
            const name = t(a.labelKey);
            const selected = draft.accent === a.hex;
            return (
              <button
                key={a.hex}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={name}
                title={name}
                onClick={() => change({ accent: a.hex })}
                className="grid size-8 place-items-center rounded-full outline-none transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-foreground/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface)]"
                style={{ background: a.hex }}
              >
                {selected && <CheckIcon className="size-4" style={{ color: onColor(a.hex) }} />}
              </button>
            );
          })}
          <ColorPopover
            label={t('settings.theme.customAccent')}
            value={draft.accent}
            onChange={(accent) => change({ accent })}
            trigger={
              <span
                className="grid size-8 place-items-center rounded-full"
                style={customAccent ? { background: draft.accent } : { background: 'rgb(var(--overlay-tint) / 0.08)' }}
              >
                {customAccent ? (
                  <CheckIcon className="size-4" style={{ color: onColor(draft.accent) }} />
                ) : (
                  <svg viewBox="0 0 24 24" className="size-4 text-ink-muted" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                )}
              </span>
            }
          />
        </div>
      </Row>

      <div className="flex flex-col gap-4">
        <button
          type="button"
          aria-expanded={fineOpen}
          onClick={() => setFineOpen((v) => !v)}
          className="flex items-center gap-2 self-start rounded-lg py-1 text-sm font-medium text-ink outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <span aria-hidden className={'inline-block text-ink-muted transition-transform ' + (fineOpen ? 'rotate-90' : '')}>
            ›
          </span>
          {t('settings.theme.fine')}
          {tuned && !fineOpen && <span className="size-1.5 rounded-full bg-accent" aria-label={t('settings.theme.fineChanged')} />}
        </button>
        {fineOpen && (
          <div className="flex flex-col gap-5 rounded-xl bg-[rgb(var(--overlay-tint)/0.04)] p-4">
            <Knob
              label={t('settings.theme.brightness')}
              low={t('settings.theme.darker')}
              high={t('settings.theme.lighter')}
              value={draft.brightness}
              onChange={(brightness) => change({ brightness })}
            />
            <Knob
              label={t('settings.theme.cards')}
              low={t('settings.theme.cardsLow')}
              high={t('settings.theme.cardsHigh')}
              value={draft.cards}
              onChange={(cards) => change({ cards })}
            />
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-ink">{t('settings.theme.customBackground')}</span>
              <ColorPopover
                label={t('settings.theme.customBackground')}
                value={draft.base}
                onChange={(base) => change({ base })}
                trigger={<span className="block size-8 rounded-lg" style={{ background: palette.surface, boxShadow: customTone ? '0 0 0 2px var(--accent)' : undefined }} />}
              />
            </div>
          </div>
        )}
      </div>
      </div>}
    </div>
  );
}

/** Readable ink on a colour fill. */
function onColor(hex: string): string {
  return contrastRatio('#151515', hex) >= contrastRatio('#ffffff', hex) ? '#151515' : '#ffffff';
}

function Row(props: { label: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-medium text-ink">{props.label}</h4>
        {props.aside}
      </div>
      {props.children}
    </section>
  );
}

function Knob(props: { label: string; low: string; high: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm text-ink">{props.label}</span>
      <Slider
        aria-label={props.label}
        minValue={0}
        maxValue={1}
        step={0.01}
        value={props.value}
        onChange={(v) => props.onChange(Array.isArray(v) ? (v[0] ?? 0) : v)}
      >
        <Slider.Track>
          <Slider.Fill />
          <Slider.Thumb />
        </Slider.Track>
      </Slider>
      <div className="flex justify-between text-[11px] text-ink-muted">
        <span>{props.low}</span>
        <span>{props.high}</span>
      </div>
    </div>
  );
}

/** A background choice drawn as the app would look in it: the side panel, the
 *  chat with two message cards, and the accent. Name underneath. */
function ToneCard(props: { name: string; palette: DerivedPalette; selected: boolean; onSelect: () => void }) {
  const p = props.palette;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      aria-label={props.name}
      onClick={props.onSelect}
      className="group flex min-w-0 flex-col gap-1.5 rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <span
        aria-hidden
        className="flex h-14 w-full gap-1 overflow-hidden rounded-xl p-1 transition-transform group-hover:scale-[1.03]"
        style={{
          background: p.outer,
          boxShadow: props.selected ? `0 0 0 2px ${p.accent}` : undefined,
        }}
      >
        <span className="w-1/4 rounded-md" style={{ background: p.surface }} />
        <span className="flex flex-1 flex-col justify-center gap-1 rounded-md px-1.5" style={{ background: p.canvas }}>
          <span className="h-2 w-3/5 rounded" style={{ background: p.surface }} />
          <span className="flex items-center justify-between gap-1">
            <span className="h-2 w-2/5 rounded" style={{ background: p.surface }} />
            <span className="h-2 w-3 rounded" style={{ background: p.accent }} />
          </span>
        </span>
      </span>
      <span className={'min-h-4 text-xs leading-4 ' + (props.selected ? 'font-medium text-ink' : 'text-ink-muted')}>{props.name}</span>
    </button>
  );
}

/** A complete miniature of the current chat. Fixed rows keep the composer
 * visible at narrow widths, without the retired stripe on a message. */
function AppPreview(props: { palette: DerivedPalette }) {
  const p = props.palette;
  const { t } = useTranslation();
  const line = (w: string, c: string, o = 1) => (
    <span className="block h-1 shrink-0 rounded-sm" style={{ width: w, background: c, opacity: o }} />
  );
  return (
    <div
      role="img"
      aria-label={t('settings.theme.preview')}
      className="app-theme-preview flex h-48 gap-1.5 overflow-hidden rounded-lg p-1.5"
      style={{ background: p.outer, boxShadow: `inset 0 0 0 1px ${p.hairline}` }}
    >
      <span aria-hidden className="flex w-[28%] min-w-0 shrink-0 flex-col gap-2 rounded-md p-2" style={{ background: p.surface }}>
        <span className="truncate text-[10px] font-semibold" style={{ color: p.ink }}>{t('dm.conversations')}</span>
        <span className="flex h-5 shrink-0 items-center rounded px-1.5" style={{ background: p.surfaceSunken }}>
          {line('70%', p.inkMuted, 0.35)}
        </span>
        <span className="flex h-4 shrink-0 items-start gap-2 border-b text-[8px]" style={{ borderColor: p.hairline, color: p.inkMuted }}>
          <span className="h-full border-b-2" style={{ borderColor: p.accent, color: p.ink }}>{t('dm.folderAll')}</span>
          <span className="truncate">{t('dm.friendsTab')}</span>
        </span>
        <span className="flex min-h-0 flex-1 flex-col gap-1">
          {[true, false, false].map((selected, i) => (
            <span key={i} className="flex h-6 shrink-0 items-center gap-1.5 rounded px-1" style={{ background: selected ? p.hover : 'transparent' }}>
              <span className="size-4 shrink-0 rounded" style={{ background: p.surfaceRaised }} />
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                {line('65%', p.ink, 0.65)}
                {line('90%', p.inkMuted, 0.3)}
              </span>
            </span>
          ))}
        </span>
      </span>
      <span aria-hidden className="flex min-w-0 flex-1 flex-col gap-2 rounded-md p-2" style={{ background: p.canvas }}>
        <span className="flex h-5 shrink-0 items-start justify-between gap-3 border-b" style={{ borderColor: p.hairline }}>
          <span className="truncate text-[10px] font-semibold" style={{ color: p.ink }}>{t('gmod.group')}</span>
          <span className="text-[10px] leading-none" style={{ color: p.inkMuted }}>···</span>
        </span>
        <span className="flex min-h-0 flex-1 flex-col justify-end gap-1.5">
          <span className="flex w-4/5 shrink-0 flex-col gap-1.5 rounded-md px-2 py-2" style={{ background: p.bubbleIn }}>
            {line('30%', p.ink, 0.65)}
            {line('90%', p.inkMuted, 0.4)}
          </span>
          <span className="flex w-2/3 shrink-0 flex-col gap-1.5 self-end rounded-md px-2 py-2" style={{ background: `color-mix(in oklab, ${p.accent} 8%, ${p.bubbleIn})` }}>
            {line('40%', p.ink, 0.65)}
            {line('80%', p.inkMuted, 0.4)}
          </span>
        </span>
        <span className="flex h-7 shrink-0 items-center gap-2 rounded-md px-2" style={{ background: p.surface }}>
          <span className="text-xs" style={{ color: p.inkMuted }}>+</span>
          <span className="min-w-0 flex-1">{line('55%', p.inkMuted, 0.3)}</span>
          <span className="grid size-4 shrink-0 place-items-center rounded text-[10px]" style={{ background: `color-mix(in oklab, ${p.accent} 15%, ${p.surface})`, color: p.accentText }}>↑</span>
        </span>
      </span>
    </div>
  );
}

/** A colour picker in a popover: saturation/brightness area, hue, HEX. Keeps
 *  its own Color so the hue survives black, white and greys (a hex has none,
 *  and re-parsing it snapped the picker to red). */
function ColorPopover(props: { label: string; value: string; onChange: (hex: string) => void; trigger: React.ReactNode }) {
  const [color, setColor] = useState<Color>(() => parseColor(props.value));
  useEffect(() => {
    if (props.value.toLowerCase() !== color.toString('hex').toLowerCase()) setColor(parseColor(props.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `color` is compared, not a trigger
  }, [props.value]);
  return (
    <ColorPicker
      value={color}
      onChange={(c) => {
        setColor(c);
        props.onChange(c.toString('hex').toLowerCase());
      }}
    >
      <ColorPicker.Trigger aria-label={props.label} className="rounded-full outline-none transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-accent">
        {props.trigger}
      </ColorPicker.Trigger>
      <ColorPicker.Popover className="gap-2">
        <ColorArea aria-label={props.label} className="max-w-full" colorSpace="hsb" xChannel="saturation" yChannel="brightness">
          <ColorArea.Thumb />
        </ColorArea>
        <ColorSlider aria-label="Hue" channel="hue" className="gap-1 px-1" colorSpace="hsb">
          <ColorSlider.Track>
            <ColorSlider.Thumb />
          </ColorSlider.Track>
        </ColorSlider>
        <ColorField aria-label="HEX">
          <ColorField.Group variant="secondary">
            <ColorField.Prefix>
              <ColorSwatch size="xs" />
            </ColorField.Prefix>
            <ColorField.Input />
          </ColorField.Group>
        </ColorField>
      </ColorPicker.Popover>
    </ColorPicker>
  );
}
